import visaSchema from "../../schemas/visaSchema";
import validationBuilder from "./validationBuilder";
import visaService from "../visaService";

const validationFunctions = {
  // Helper method to identify if this is a passport upload question
  isPassportUploadQuestion: (question) => {
    if (!question || question.type !== "uploadFile") return false;
    
    // Check if the question key or label contains passport-related terms
    return question.key === "passportImage";
  },

  // Helper method to validate passport upload via API
  validatePassportUpload: async (file) => {
    if (!file) {
      return { error: "נא להעלות תמונת דרכון" };
    }

    // Basic file type and size validation before API call
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      return { error: "סוג הקובץ לא נתמך. נא להעלות תמונה (JPG, PNG) או קובץ PDF" };
    }

    try {
      const apiResponse = await visaService.validatePassport(file);
      
      // If there's a network error or API error, allow validation to pass
      if (apiResponse.err) {
        console.warn("Passport validation API error, allowing validation to pass:", apiResponse.err);
        return { 
          error: null, 
          validationData: null,
          apiError: true // Flag to indicate API was not available
        };
      }

      // Handle parsing errors from the API - also allow validation to pass
      if (apiResponse.error) {
        console.warn("Passport validation parsing error, allowing validation to pass:", apiResponse.error);
        return { 
          error: null, 
          validationData: null,
          apiError: true // Flag to indicate API had parsing issues
        };
      }

      // Only block if we get a valid response with valid: false
      if (apiResponse.valid === false) {
        // Format the validation response for display
        let errorMessage = "תמונת הדרכון לא תקינה:\n";
        
        if (apiResponse.reasons && apiResponse.reasons.length > 0) {
          errorMessage += "• " + apiResponse.reasons.join("\n• ");
        }
        
        if (apiResponse.suggestions && apiResponse.suggestions.length > 0) {
          errorMessage += "\n\nהמלצות לתיקון:\n";
          errorMessage += "• " + apiResponse.suggestions.join("\n• ");
        }

        return { 
          error: errorMessage,
          validationData: apiResponse // Store full response for potential use
        };
      }

      return { 
        error: null, 
        validationData: apiResponse // Store the successful validation data
      };
    } catch (error) {
      // Network errors or any other errors should allow validation to pass
      console.warn("Passport validation network error, allowing validation to pass:", error);
      return { 
        error: null, 
        validationData: null,
        apiError: true // Flag to indicate API was not available
      };
    }
  },
  validateOneField: (question, val) => {
    return new Promise(async (resolve, reject) => {
      try {
        if (question.type === "radioBtns" || question.type === "radioCircles" || question.type === "autocomplete") {
          return resolve(val);
        }
        if (question === "initEmail") {
          question = {
            key: "email",
            validation: {
              validationType: "txtType",
              trim: true,
              email: true,
              lowercase: true,
              min: "5",
              max: "40"
            }
          };
        }
        var key = question.key;

        // Phone must start with 05 or 97205
        if (key === "phoneNumber") {
          if (!/^(05|97205)/.test(val)) {
            return reject({ message: "המספר חייב להתחיל ב-05" });
          } else if (val.length < 10) {
            return reject({ message: "חייב להיות באורך של 10 מספרים" });
          }
        }

        if (question.validation.validationType === "fileType") {
          // Add passport-specific validation if this is a passport upload question
          if (validationFunctions.isPassportUploadQuestion(question)) {
            try {
              const passportValidation = await validationFunctions.validatePassportUpload(val);
              if (passportValidation.error) {
                return reject({ message: passportValidation.error });
              }
              // If validation is successful, include the validation data in the response
              return resolve({ 
                [question.key]: val, 
                passportValidationData: passportValidation.validationData 
              });
            } catch (error) {
              return reject({ message: "שגיאה בבדיקת הדרכון. נא לנסות שוב" });
            }
          }
          return resolve({ [question.key]: val });
        }

        var getSchema = validationBuilder(question.key, question.validation);
        var validatedData;

        // if (getSchema._nodes.includes(key)) {
        var yupObject = {};
        yupObject[key] = val;
        validatedData = await getSchema.validateAt(key, yupObject);
        // }

        return resolve(validatedData);
      } catch (validationErr) {
        return reject(validationErr);
      }
    });
  },

  validateSchema: async (schema, data) => {
    return new Promise(async (resolve, reject) => {
      try {
        const validatedData = await schema.validate(data, { abortEarly: false });
        return resolve(validatedData);
      } catch (validationErrors) {
        const getErrs = validationErrors.inner.map((item) => {
          return { key: [item.params.path], val: item.message };
        });
        const errsObj = {};
        for (const currentErr of getErrs) {
          if (errsObj[currentErr.key]) continue;
          errsObj[currentErr.key] = errsObj[currentErr.key] = currentErr.val;
        }
        return reject(errsObj);
      }
    });
  },

  /// currently not in use -> maybe i will make it a validator for an array ///
  validateObject: (fieldsObj) => {
    return new Promise(async (resolve, reject) => {
      try {
        // MAybe i will change this to also return the validated data.
        for (const key in fieldsObj) {
          var validatedData;
          var getSchema = visaSchema();
          if (getSchema._nodes.includes(key)) {
            var yupObject = {};
            yupObject[key] = fieldsObj[key];
            validatedData = await getSchema.validateAt(key, yupObject);
          } else {
            // the schema is not updated and does not know about this field... let it continue if not empty
          }
        }

        return resolve();
      } catch (validationErr) {
        return reject(validationErr);
      }
    });
  }
};

export default validationFunctions;
